import {
  Input,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { format as formatTZ, toDate } from "date-fns-tz";
import PropTypes from "prop-types";
import React from "react";

import {
  judgeList,
  languageType,
  reportLabel,
  reportOtherLabel,
  reportTableColor,
  subTableType,
  trReferenceTableLabel,
} from "../../../model/Report";
import useStyles from "../useStyles";

/**
 * ReportInputTableコンポーネント.
 *
 * @return {object} コンポーネント
 */
function ReportInputTable(props) {
  // 引数
  const {
    // 診断カルテ書式
    categoryList,
    // 診断カルテデータ(今回)
    temporaryData,
    // 診断カルテデータ(前回)
    beforeData1,
    // 診断カルテデータ(前々回)
    beforeData2,
    // 判定結果、備考更新時の処理
    onChange,
    // その他、所見等更新時の処理
    onChangeOther,
  } = props;

  // スタイル
  const styles = useStyles();

  // 表示言語
  const language = languageType.ja;

  // 取得データから見出し行に表示する日付情報を抽出
  const serachDate = (data) => {
    if (data && data.date) {
      const date = formatTZ(toDate(new Date(data.date)), "yyyy/MM/dd", {
        timeZone: "Asia/Tokyo",
      });
      return `(${date})`;
    }
    return "(YYYY/MM/DD)";
  };

  // 改行文字(\n)をHTMLタグ(<br />)に変換
  const convertText = (texts, key, subTable) => {
    const textList = texts.split(/(\n)/).map((item, index) => {
      const itemKey = `text_${index}`;

      let text = item;
      if (item.match(/\n/)) {
        // 改行文字(\n)の場合はHTMLタグ(<br />)に変換
        text = React.createElement("br");
      }

      if (item === "(YYYY/MM/DD)") {
        // 日付文字列の場合は、列に応じたデータから日付情報を取得
        if (key === "currentData") {
          text = serachDate(temporaryData);
        }
        if (key === "beforeData1") {
          text = serachDate(beforeData1);
        }
        if (key === "beforeData2") {
          text = serachDate(beforeData2);
        }
      }

      // 処理した文字列を配列に返す
      return <React.Fragment key={itemKey}>{text}</React.Fragment>;
    });

    if (subTable === subTableType.motor) {
      return (
        <>
          <div>{textList}</div>
          <Table className={styles.reportInputSubTable} size="small">
            <TableHead className={styles.reportInputSubTableHead}>
              <TableRow>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={2}
                  colSpan={2}
                />
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={2}
                >
                  {trReferenceTableLabel.motorType[language]}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={1}
                >
                  {trReferenceTableLabel.linear[language]}
                </TableCell>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={1}
                >
                  {trReferenceTableLabel.rotary[language]}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={styles.reportInputSubTableBody}>
              <TableRow>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={3}
                  colSpan={1}
                >
                  {trReferenceTableLabel.judge[language]}
                </TableCell>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={1}
                >
                  {trReferenceTableLabel.A[language]}
                </TableCell>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={1}
                >
                  {key === "trErrorTemp"
                    ? trReferenceTableLabel.linearTrErrorTempJudgeA[language]
                    : trReferenceTableLabel.otherJudgeA[language]}
                </TableCell>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={1}
                >
                  0
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={1}
                >
                  {trReferenceTableLabel.B[language]}
                </TableCell>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={1}
                >
                  {key === "trErrorTemp"
                    ? trReferenceTableLabel.linearTrErrorTempJudgeB[language]
                    : trReferenceTableLabel.otherJudgeB[language]}
                </TableCell>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={1}
                >
                  1 ～
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={1}
                >
                  {trReferenceTableLabel.C[language]}
                </TableCell>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={1}
                >
                  {key === "trErrorTemp"
                    ? trReferenceTableLabel.linearTrErrorTempJudgeC[language]
                    : trReferenceTableLabel.otherJudgeC[language]}
                </TableCell>
                <TableCell
                  className={styles.reportInputSubTableCell}
                  align="center"
                  rowSpan={1}
                  colSpan={1}
                >
                  3 ～
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </>
      );
    }
    return <div>{textList}</div>;
  };

  // 取得データから行に対応する情報を取得(値)
  const serachValue = (data, category, item) => {
    if (data && category && category.key) {
      const items = data[category.key];

      if (item.key && items && items[item.key]) {
        if (items[item.key].diff) {
          return `${item.beforeUnit[language]}${items[item.key].diff} ${
            item.unit[language]
          }`;
        }
        return `${item.beforeUnit[language]}${items[item.key].value}${
          item.unit[language]
        }`;
      }
    }
    return item.unit[language];
  };

  // 取得データから行に対応する情報を取得(判定)
  const serachJudge = (data, category, item) => {
    if (data && category) {
      const items = data[category];

      if (items && items[item]) {
        return items[item].judge;
      }
    }
    return "";
  };

  // 判定結果の文字色判定
  const changeJudgeColor = (data, category, item) => {
    let color = "black";

    if (data && data[category] && data[category][item]) {
      switch (data[category][item].judge) {
        case "A":
        case "A.":
          color = reportTableColor.levelA;
          break;
        case "B":
        case "B.":
          color = reportTableColor.levelB;
          break;
        case "C":
        case "C.":
          color = reportTableColor.levelC;
          break;
        default:
          color = "black";
          break;
      }
    }
    return color;
  };

  // 値、判定列の生成
  const createDataColumn = (category, item) => (
    <>
      <TableCell
        className={`${styles.reportInputTableCell} ${styles.reportInputTableColumnItem}`}
        align={item.align}
        style={{ backgroundColor: reportTableColor.currentData }}
      >
        {item.label[language]}
      </TableCell>
      <TableCell
        className={`${styles.reportInputTableCell} ${styles.reportInputTableColumnValue}`}
        align="right"
        style={{ backgroundColor: reportTableColor.currentData }}
      >
        {serachValue(temporaryData, category, item)}
      </TableCell>
      <TableCell
        className={`${styles.reportInputTableCell} ${styles.reportInputTableColumnJudge}`}
        align="center"
        style={{ backgroundColor: reportTableColor.inputData }}
      >
        <Select
          className={`${styles.reportTableInput} ${styles.reportInputTableSelect}`}
          name={`${category.key}.${item.key}`}
          value={serachJudge(temporaryData, category.key, item.key)}
          onChange={onChange}
          disableUnderline
          style={{
            color: changeJudgeColor(temporaryData, category.key, item.key),
          }}
          disabled={!item.judge}
        >
          {judgeList.map((level) => (
            <MenuItem
              key={`${category.key}.${item.key}.${level.value}`}
              value={level.value}
              style={
                level.value.indexOf(".") !== -1
                  ? undefined
                  : { display: "none" }
              }
            >
              {level.label}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell
        className={`${styles.reportInputTableCell} ${styles.reportInputTableColumnValue}`}
        align="right"
        style={{ backgroundColor: reportTableColor.beforeData1 }}
      >
        {serachValue(beforeData1, category, item)}
      </TableCell>
      <TableCell
        className={`${styles.reportInputTableCell} ${styles.reportInputTableColumnJudge}`}
        align="center"
        style={{
          backgroundColor: reportTableColor.beforeData1,
          color: changeJudgeColor(beforeData1, category.key, item.key),
        }}
      >
        {serachJudge(beforeData1, category.key, item.key)}
      </TableCell>
      <TableCell
        className={`${styles.reportInputTableCell} ${styles.reportInputTableColumnValue}`}
        align="right"
        style={{ backgroundColor: reportTableColor.beforeData2 }}
      >
        {serachValue(beforeData2, category, item)}
      </TableCell>
      <TableCell
        className={`${styles.reportInputTableCell} ${styles.reportInputTableColumnJudge}`}
        align="center"
        style={{
          backgroundColor: reportTableColor.beforeData2,
          color: changeJudgeColor(beforeData2, category.key, item.key),
        }}
      >
        {serachJudge(beforeData2, category.key, item.key)}
      </TableCell>
    </>
  );

  const createCategoryRow = (category, parentIndex) => (
    <React.Fragment key={category.key || `category-${parentIndex}`}>
      {category.item.map((item, index) => (
        <TableRow
          className={styles.reportInputTableRow}
          key={`${category.key}.${item.key}`}
        >
          {index === 0 && (
            <>
              <TableCell
                className={`${styles.reportInputTableCell} ${styles.reportInputTableColumnNumber}`}
                align={category.align}
                rowSpan={category.rowSpan}
                colSpan={category.colSpan}
                style={{
                  backgroundColor: reportTableColor.currentData,
                }}
              >
                {String(parentIndex + 1)}
              </TableCell>
              <TableCell
                className={`${styles.reportInputTableCell} ${styles.reportInputTableColumnCategory}`}
                align={category.align}
                rowSpan={category.rowSpan}
                colSpan={category.colSpan}
                style={{
                  backgroundColor: reportTableColor.currentData,
                }}
              >
                {convertText(category.label[language], null, null)}
              </TableCell>
            </>
          )}
          {createDataColumn(category, item)}
          {index === 0 && (
            <>
              <TableCell
                className={`${styles.reportInputTableCell} ${styles.reportInputTableColumnNote}`}
                align={category.align}
                rowSpan={category.rowSpan}
                colSpan={category.colSpan}
                style={{
                  height: 50 * category.rowSpan,
                  backgroundColor: reportTableColor.inputData,
                }}
              >
                <Input
                  className={styles.reportInputTableInput}
                  name={`${category.key}.note`}
                  value={
                    temporaryData[category.key] &&
                    temporaryData[category.key].note
                      ? temporaryData[category.key].note
                      : ""
                  }
                  onChange={onChange}
                  margin="none"
                  fullWidth
                  multiline
                  disableUnderline
                />
              </TableCell>
              <TableCell
                className={`${styles.reportInputTableCell} ${styles.reportInputTableColumnExplanation}`}
                align={category.align}
                rowSpan={
                  categoryList.length === parentIndex + 1
                    ? category.rowSpan + 1
                    : category.rowSpan
                }
                colSpan={category.colSpan}
                style={{
                  backgroundColor: reportTableColor.default,
                }}
              >
                {convertText(
                  category.explanation[language],
                  category.key,
                  category.subTable
                )}
              </TableCell>
            </>
          )}
        </TableRow>
      ))}
    </React.Fragment>
  );

  return (
    <>
      {categoryList ? (
        <TableContainer className={styles.reportInputTableContainer}>
          <Table
            className={styles.reportInputTable}
            size="small"
            aria-label="report-data-table"
          >
            <TableHead className={styles.reportInputTableHead}>
              <TableRow className={styles.reportInputTableRow}>
                {reportLabel.map((item) => (
                  <TableCell
                    className={styles.reportInputTableCell}
                    key={item.key}
                    align={item.align}
                    rowSpan={item.rowSpan}
                    colSpan={item.colSpan}
                    style={{ backgroundColor: item.color }}
                  >
                    {convertText(item.label[language], item.key, null)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody className={styles.reportInputTableBody}>
              {categoryList.map((category, index) =>
                createCategoryRow(category, index)
              )}
              <TableRow
                style={{
                  height: 100,
                }}
              >
                <TableCell
                  className={styles.reportInputTableCell}
                  align="left"
                  rowSpan={1}
                  colSpan={2}
                  style={{
                    height: 100,
                    backgroundColor: reportTableColor.default,
                  }}
                >
                  {convertText(reportOtherLabel[language], null, null)}
                </TableCell>
                <TableCell
                  className={styles.reportInputTableCell}
                  align="left"
                  rowSpan={1}
                  colSpan={8}
                  style={{
                    height: 100,
                    backgroundColor: reportTableColor.inputData,
                  }}
                >
                  <Input
                    className={styles.reportInputTableInput}
                    name="other"
                    value={
                      temporaryData && temporaryData.other
                        ? temporaryData.other
                        : ""
                    }
                    onChange={onChangeOther}
                    margin="none"
                    fullWidth
                    multiline
                    disableUnderline
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <></>
      )}
    </>
  );
}

ReportInputTable.propTypes = {
  categoryList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  temporaryData: PropTypes.shape().isRequired,
  beforeData1: PropTypes.shape().isRequired,
  beforeData2: PropTypes.shape().isRequired,
  onChange: PropTypes.func.isRequired,
  onChangeOther: PropTypes.func.isRequired,
};

export default ReportInputTable;
